import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { publicAPI, getDetailedInsights } from '../services/api';
import {
  BarChart,
  Bar,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import SEO from '../components/SEO';

interface PublicInsightsSummary {
  total_contracts: number;
  total_municipalities: number;
  total_industries: number;
  total_value: number | null;
}

interface IndustryData {
  industry: string;
  count: number;
}

interface MunicipalityData {
  name: string;
  count: number;
}

interface PublicInsightsData {
  summary: PublicInsightsSummary;
  top_industries: IndustryData[];
  top_municipalities: MunicipalityData[];
}

interface TimeSeriesData {
  date: string;
  count: number;
}

interface DetailedInsightsData {
  contracts_over_time: TimeSeriesData[];
  all_industries: IndustryData[];
  all_municipalities: MunicipalityData[];
  filters_applied: {
    industry: string | null;
    municipality: string | null;
    days_back: number;
  };
}

interface AxisTickProps {
  x?: number;
  y?: number;
  payload?: {
    value?: string;
  };
}

const Insights: React.FC = () => {
  const { user } = useAuth();
  const [publicData, setPublicData] = useState<PublicInsightsData | null>(null);
  const [detailedData, setDetailedData] = useState<DetailedInsightsData | null>(null);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state for authenticated users
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');
  const [selectedMunicipality, setSelectedMunicipality] = useState<string>('');
  const [daysBack, setDaysBack] = useState<number>(30);
  const barColors = [
    '#2563eb',
    '#16a34a',
    '#f97316',
    '#0ea5e9',
    '#e11d48',
    '#8b5cf6',
    '#14b8a6',
    '#f59e0b',
    '#22c55e',
    '#6366f1',
  ];

  const wrapLabel = (label: string, maxChars: number, maxLines: number): string[] => {
    if (!label) return [''];
    if (label.length <= maxChars) return [label];

    const words = label.split(' ');
    const lines: string[] = [];
    let current = '';

    words.forEach((word) => {
      const next = current ? `${current} ${word}` : word;
      if (next.length <= maxChars) {
        current = next;
      } else {
        if (current) {
          lines.push(current);
        }
        current = word;
      }
    });

    if (current) {
      lines.push(current);
    }

    if (lines.length <= maxLines) {
      return lines;
    }

    const head = lines.slice(0, maxLines - 1);
    const tail = lines.slice(maxLines - 1).join(' ');
    const trimmed = tail.length > maxChars ? `${tail.slice(0, maxChars - 3).trimEnd()}...` : tail;
    return [...head, trimmed];
  };

  const XAxisTick: React.FC<AxisTickProps> = ({ x = 0, y = 0, payload }) => {
    const label = String(payload?.value ?? '');
    const lines = wrapLabel(label, 16, 2);
    const lineHeight = 14;

    return (
      <text x={x} y={y + 12} textAnchor="middle" fill="#6b7280" fontSize={12}>
        {lines.map((line, index) => (
          <tspan key={`${label}-${index}`} x={x} dy={index === 0 ? 0 : lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  // Load public data
  useEffect(() => {
    let isMounted = true;

    publicAPI
      .getInsights()
      .then(({ data }) => {
        if (isMounted) {
          setPublicData(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err.message || 'Failed to load insights');
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Load detailed data for authenticated users
  useEffect(() => {
    if (!user) {
      setDetailedData(null);
      setDetailedLoading(false);
      return;
    }

    let isMounted = true;
    setDetailedLoading(true);

    const params = new URLSearchParams();
    if (selectedIndustry) params.append('industry', selectedIndustry);
    if (selectedMunicipality) params.append('municipality', selectedMunicipality);
    params.append('days_back', daysBack.toString());

    getDetailedInsights(params.toString())
      .then(({ data }) => {
        if (isMounted) {
          setDetailedData(data);
        }
      })
      .catch((err) => {
        console.error('Failed to load detailed insights:', err);
      })
      .finally(() => {
        if (isMounted) {
          setDetailedLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [user, selectedIndustry, selectedMunicipality, daysBack]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading insights...</p>
        </div>
      </div>
    );
  }

  if (error || !publicData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Failed to load insights. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Contract Insights - Massachusetts Government Contracting Data"
        description="Explore real-time data on government contract opportunities across Massachusetts. See industry trends, municipality activity, and contract values."
        canonical="/insights"
      />

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Massachusetts Contract Insights
            </h1>
            <p className="mt-2 text-lg text-gray-600">
              Real-time data on government contracting opportunities across the state of Massachusetts
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-1">
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Active Opportunities
                    </dt>
                    <dd className="mt-1 text-3xl font-semibold text-gray-900">
                      {publicData.summary.total_contracts.toLocaleString()}
                    </dd>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-1">
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Procuring Organizations
                    </dt>
                    <dd className="mt-1 text-3xl font-semibold text-gray-900">
                      {publicData.summary.total_municipalities.toLocaleString()}
                    </dd>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-1">
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Industries
                    </dt>
                    <dd className="mt-1 text-3xl font-semibold text-gray-900">
                      {publicData.summary.total_industries.toLocaleString()}
                    </dd>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Top 10 Industries Chart */}
          <div className="bg-white shadow rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Top 10 Industries by Opportunity Count
            </h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={publicData.top_industries} margin={{ top: 5, right: 30, left: 20, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="industry"
                  type="category"
                  interval={0}
                  height={60}
                  tickLine={false}
                  tick={<XAxisTick />}
                />
                <YAxis type="number" />
                <Tooltip />
                <Bar dataKey="count">
                  {publicData.top_industries.map((entry, index) => (
                    <Cell key={`industry-bar-${entry.industry}`} fill={barColors[index % barColors.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Top 10 Municipalities Chart */}
          <div className="bg-white shadow rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Top 10 Procuring Organizations by Opportunity Count
            </h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={publicData.top_municipalities} margin={{ top: 5, right: 30, left: 20, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  type="category"
                  interval={0}
                  height={60}
                  tickLine={false}
                  tick={<XAxisTick />}
                />
                <YAxis type="number" />
                <Tooltip />
                <Bar dataKey="count">
                  {publicData.top_municipalities.map((entry, index) => (
                    <Cell key={`municipality-bar-${entry.name}`} fill={barColors[index % barColors.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Gated Content for Authenticated Users */}
          {user ? (
            detailedLoading ? (
              <div className="bg-white shadow rounded-lg p-6 mb-8">
                <p className="text-gray-500 text-center">Loading detailed insights...</p>
              </div>
            ) : detailedData ? (
              <>
                {/* Filters */}
                <div className="bg-white shadow rounded-lg p-6 mb-8">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Filter Data
                  </h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label htmlFor="industry" className="block text-sm font-medium text-gray-700">
                        Industry
                      </label>
                      <select
                        id="industry"
                        value={selectedIndustry}
                        onChange={(e) => setSelectedIndustry(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="">All Industries</option>
                        {detailedData.all_industries.map((ind) => (
                          <option key={ind.industry} value={ind.industry}>
                            {ind.industry} ({ind.count})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="municipality" className="block text-sm font-medium text-gray-700">
                        Organization
                      </label>
                      <select
                        id="municipality"
                        value={selectedMunicipality}
                        onChange={(e) => setSelectedMunicipality(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="">All Organizations</option>
                        {detailedData.all_municipalities.slice(0, 50).map((muni) => (
                          <option key={muni.name} value={muni.name}>
                            {muni.name} ({muni.count})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="daysBack" className="block text-sm font-medium text-gray-700">
                        Time Period
                      </label>
                      <select
                        id="daysBack"
                        value={daysBack}
                        onChange={(e) => setDaysBack(Number(e.target.value))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value={7}>Last 7 days</option>
                        <option value={14}>Last 14 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={60}>Last 60 days</option>
                        <option value={90}>Last 90 days</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Contracts Over Time */}
                <div className="bg-white shadow rounded-lg p-6 mb-8">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    New Opportunities Over Time
                  </h2>
                  {detailedData.contracts_over_time.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={detailedData.contracts_over_time}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="count" stroke="#4f46e5" name="Opportunities" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      No opportunities found for the selected time period and filters.
                    </p>
                  )}
                </div>
              </>
            ) : null
          ) : null}

          {/* Footer CTA */}
          <div className="mt-12 bg-white shadow rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Ready to find your next opportunity?
            </h2>
            <p className="text-gray-600 mb-6">
              Browse all active opportunities and start bidding today.
            </p>
            <Link
              to="/opportunities"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Browse Opportunities
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Insights;
